import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import httpInstances from '@/api/axiosInstance';
import { CAPTCHA_HEADER } from '@/pages/Onboarding/hooks/useReCaptchaToken';
import { extractErrorMessage } from '@/utils/extractErrorMessage';
import { handleThunkError } from '@/utils/thunkErrorHandler';

const CONFIG_NAME = 'userCreation';

export type User = {
  email: string;
  hs_firstname: string;
  hs_lastname: string;
  gxgo_declared_data_usage: string;
  hs_company: string;
  prefMarket: string;
};

type UserCreationState = {
  status: 'idle' | 'loading' | 'failed' | 'fulfilled';
  error: string | null;
};

const initialState: UserCreationState = {
  status: 'idle',
  error: null
};
type Request = { user: User; captchaToken: string; verificationToken: string };

export const createUser = createAsyncThunk(
  'createUser',
  async (request: Request, { rejectWithValue }) => {
    try {
      const response = await postUser(request);
      if (response.status !== 200) {
        return rejectWithValue("Couldn't create user");
      }
      return { status: response.status, data: response.data };
    } catch (error) {
      return handleThunkError(error, rejectWithValue) as never;
    }
  }
);
const userCreationSlice = createSlice({
  name: CONFIG_NAME,
  initialState,
  reducers: {
    resetStatus(state) {
      state.status = 'idle';
    }
  },
  extraReducers: builder => {
    builder
      .addCase(createUser.pending, state => {
        state.status = 'loading';
      })
      .addCase(createUser.fulfilled, state => {
        state.status = 'fulfilled';
      })
      .addCase(createUser.rejected, (state, action) => {
        state.status = 'failed';
        state.error = extractErrorMessage(action.payload, action.error.message);
      });
  }
});
export const { resetStatus } = userCreationSlice.actions;
export default userCreationSlice.reducer;

const postUser = async (request: Request) => {
  return httpInstances.axiosGoInstance.post(
    `/users?verificationToken=${request.verificationToken}`,
    request.user,
    {
      headers: { [CAPTCHA_HEADER]: request.captchaToken }
    }
  );
};
